import { Injectable, computed, signal } from '@angular/core';
import { ConversationContext } from '@raptor-fe/core';

/** Lifecycle of the single floating conversation window. */
export type WindowState = 'closed' | 'open' | 'minimized';

/**
 * Root-scoped owner of the single live conversation window (Spec #76394 Frontends part). Enforces
 * the singleton rule from the Spec: at most one window ever exists across the whole app.
 *
 * The window has three states -- closed, open (floating), and minimized (docked to the bottom-right
 * as a pill). Opening a DIFFERENT customer swaps the context; opening (or clicking the dock pill for)
 * the SAME customer restores/focuses the existing window without rebuilding it.
 *
 * Ported from `omni-web-mockups/customer-messaging/customer-text-window.service.ts` verbatim (Tier 2);
 * the only change is that `ConversationContext` is imported from the shared `@raptor-fe/core` model
 * rather than the mockup's local `models.ts`.
 */
@Injectable({ providedIn: 'root' })
export class CustomerTextWindowService {
    private readonly _context = signal<ConversationContext | null>(null);
    private readonly _state = signal<WindowState>('closed');

    /** The currently loaded conversation context, or null when closed. */
    readonly context = this._context.asReadonly();

    /** closed | open | minimized. */
    readonly state = this._state.asReadonly();

    /** True while the floating window is visible (not minimized, not closed). */
    readonly isOpen = computed(() => this._state() === 'open');

    /** True while docked to the bottom-right as a pill. */
    readonly isMinimized = computed(() => this._state() === 'minimized');

    /** Pulsed when a same-customer open restores/focuses the existing window. */
    readonly focusPing = signal(0);

    open(context: ConversationContext): void {
        const current = this._context();
        const sameCustomer = current && current.customerId === context.customerId;
        if (sameCustomer && this._state() !== 'closed') {
            this._state.set('open');
            this.focusPing.update((n) => n + 1);
            return;
        }
        this._context.set(context);
        this._state.set('open');
    }

    minimize(): void {
        if (this._state() === 'open') {
            this._state.set('minimized');
        }
    }

    restore(): void {
        if (this._state() === 'minimized') {
            this._state.set('open');
            this.focusPing.update((n) => n + 1);
        }
    }

    close(): void {
        this._state.set('closed');
        this._context.set(null);
    }
}
